import React, {useEffect, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {Add, ArrowBack, Delete, ErrorOutline, Flight, FlightTakeoff, Person, Schedule} from '@mui/icons-material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import {t} from 'ttag';
import {useMask} from '@react-input/mask';
import {DateTime} from 'luxon';
import useFadeNavigate from './useFadeNavigate';
import {useGetScheduledTicketUsageDeclarationsMutation, useGetTicketInfoMutation} from '../../store/apis/ccs';
import {ScheduledDeclarationInfo} from '../../store/apis/ccs/declarationsEndpoints';

interface TicketEntry {
  company: string;
  number: string;
  documentInfo: any;
  documentType: string;
  idNumber: string;
  nif: string;
}

const COUPON_STATUS_LABELS: Record<string, string> = {
  O: t`Open`,
  U: t`Flown`,
  E: t`Reissued`,
  R: t`Refunded`,
  X: t`Cancelled`,
  A: t`Expired`,
};

const COUPON_STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  O: 'default',
  U: 'success',
  E: 'warning',
  R: 'warning',
  X: 'default',
  A: 'default',
};

const COUPON_STATUS_BORDER: Record<string, string> = {
  O: 'divider',
  U: 'success.main',
  E: 'warning.main',
  R: 'warning.main',
  X: 'divider',
  A: 'divider',
};

const COUPON_STATUS_BG: Record<string, string> = {
  O: 'transparent',
  U: 'rgba(46,125,50,0.04)',
  E: 'rgba(237,108,2,0.04)',
  R: 'rgba(237,108,2,0.04)',
  X: 'transparent',
  A: 'transparent',
};

const DOCUMENT_TYPES = [
  {value: 'CC', label: 'CC'},
  {value: 'BI', label: 'BI'},
  {value: 'P', label: t`Passport`},
  {value: 'RR', label: t`Residence Permit`},
  {value: 'OTHER', label: t`Other`},
];

function extractDateString(raw: any): string | null {
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw.dateTime) return raw.dateTime;
  return null;
}

function formatCouponDateOnly(raw: any): string {
  const str = extractDateString(raw);
  if (!str) return '';
  const dt = DateTime.fromISO(str);
  return dt.isValid ? dt.toFormat('dd/MM/yyyy') : str;
}

function getPassengerName(passenger: any): string {
  if (!passenger) return '';
  return [passenger.firstName, passenger.lastName].filter(Boolean).join(' ');
}

function tabLabel(company: string, number: string): string {
  if (number.length <= 6) return `${company}-${number}`;
  return `${company}-${number.slice(0, 3)}...${number.slice(-3)}`;
}

function buildInitialTickets(state: any): TicketEntry[] {
  return (state?.tickets ?? []).map((tk: any) => ({
    company: tk.company ?? tk.documentInfo?.companyCode ?? '',
    number: tk.number ?? tk.documentInfo?.documentNumber ?? '',
    documentInfo: tk.documentInfo ?? {},
    documentType: tk.documentType ?? 'CC',
    idNumber: tk.idNumber ?? '',
    nif: tk.nif ?? '',
  }));
}

const TicketUsageResultPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();

  const initial = buildInitialTickets(location.state);

  const [tickets, setTickets] = useState<TicketEntry[]>(initial);
  const [activeIndex, setActiveIndex] = useState(Math.max(0, initial.length - 1));
  const [showValidation, setShowValidation] = useState(false);

  const [addTicketNumber, setAddTicketNumber] = useState('');
  const [addTicketError, setAddTicketError] = useState<string | null>(null);
  const [getTicketInfo, {isLoading: isSearching}] = useGetTicketInfoMutation();
  const [getScheduledDeclarations] = useGetScheduledTicketUsageDeclarationsMutation();
  const [schedulesByTicket, setSchedulesByTicket] = useState<Record<string, ScheduledDeclarationInfo[]>>({});
  const [checkingSchedules, setCheckingSchedules] = useState<Record<string, boolean>>({});
  const addInputRef = useMask({mask: '___-__________', replacement: {_: /\d/}});
  const addInputRefTop = useMask({mask: '___-__________', replacement: {_: /\d/}});

  useEffect(() => {
    tickets.forEach((tk) => {
      const key = `${tk.company}-${tk.number}`;
      if (key in schedulesByTicket) return;
      setSchedulesByTicket((prev) => ({...prev, [key]: []}));
      setCheckingSchedules((prev) => ({...prev, [key]: true}));
      getScheduledDeclarations({company: tk.company, number: tk.number}).then((result: any) => {
        if (result.data) {
          setSchedulesByTicket((prev) => ({...prev, [key]: result.data}));
        }
        setCheckingSchedules((prev) => ({...prev, [key]: false}));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets.map((tk) => `${tk.company}-${tk.number}`).join(',')]);

  if (tickets.length === 0) {
    return <Navigate to='/declarations/ticket-usage' replace />;
  }

  const active = tickets[activeIndex];
  const {company, number, documentInfo, documentType, idNumber, nif} = active;
  const coupons: any[] = documentInfo?.coupons ?? [];
  const passenger = documentInfo?.passenger ?? {};
  const passengerName = getPassengerName(passenger);
  const reservationCode = documentInfo?.reservationCode ?? '';
  const ticketLabel = `${company}-${number}`;
  const isIdentityValid = idNumber.trim().length > 0 || nif.trim().length > 0;
  const allTicketsValid = tickets.every((tk) => tk.idNumber.trim().length > 0 || tk.nif.trim().length > 0);
  const activeSchedules = schedulesByTicket[`${company}-${number}`] ?? [];
  const latestSchedule = activeSchedules[0] ?? null;
  const isCheckingSchedule = checkingSchedules[`${company}-${number}`] ?? true;

  const updateField = (field: keyof TicketEntry, value: string) => {
    setTickets((prev) => prev.map((tk, i) => (i === activeIndex ? {...tk, [field]: value} : tk)));
    setShowValidation(false);
  };

  const handleRemoveCurrent = () => {
    const updated = tickets.filter((_, i) => i !== activeIndex);
    if (updated.length === 0) {
      navigate('/declarations/ticket-usage', {replace: true});
    } else {
      setTickets(updated);
      setActiveIndex(Math.min(activeIndex, updated.length - 1));
    }
  };

  const handleAddAnother = () => {
    if (!addTicketNumber.match(/^\d{3}-\d{10}$/)) {
      setAddTicketError(t`Invalid format. Use XXX-XXXXXXXXXX (e.g. 153-1234567890).`);
      return;
    }
    const [addCompany, addNumber] = addTicketNumber.split('-');
    const isDuplicate = tickets.some((tk) => tk.company === addCompany && tk.number === addNumber);
    if (isDuplicate) {
      setAddTicketError(t`This ticket is already in the order.`);
      return;
    }
    setAddTicketError(null);
    getTicketInfo({company: addCompany, number: addNumber}).then(({data: ticketData}: any) => {
      if (ticketData) {
        const newEntry: TicketEntry = {
          ...ticketData,
          company: addCompany,
          number: addNumber,
          documentInfo: ticketData.documentInfo ?? ticketData,
          documentType: 'CC',
          idNumber: '',
          nif: '',
        };
        setTickets((prev) => {
          const updated = [...prev, newEntry];
          setActiveIndex(updated.length - 1);
          return updated;
        });
        setAddTicketNumber('');
      } else {
        setAddTicketError(t`Ticket not found. Please check the number and try again.`);
      }
    });
  };

  const handleContinue = () => {
    if (!allTicketsValid) {
      setShowValidation(true);
      const firstInvalidIndex = tickets.findIndex((tk) => tk.idNumber.trim().length === 0 && tk.nif.trim().length === 0);
      if (firstInvalidIndex !== -1) setActiveIndex(firstInvalidIndex);
      return;
    }
    navigate('/declarations/ticket-usage/delivery', {state: {completedTickets: tickets}});
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>

      {/* ── Page header ── */}
      <Card elevation={2} sx={{borderTop: '3px solid', borderColor: 'primary.main'}}>
        <CardContent sx={{py: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Box sx={{
              background: 'linear-gradient(135deg, #3f51b5 0%, #283593 100%)',
              color: 'white',
              borderRadius: 2,
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(63,81,181,0.45)',
            }}>
              <ConfirmationNumberIcon sx={{fontSize: '1.35rem'}} />
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography variant='h6' fontWeight='bold' lineHeight={1.2} sx={{mb: 0.75}}>
                {tickets.length === 1 ? t`1 ticket in order` : `${tickets.length} ${t`tickets in order`}`}
              </Typography>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.4}}>
                {tickets.map((tk, i) => {
                  const tkName = getPassengerName(tk.documentInfo?.passenger);
                  const tkPnr = tk.documentInfo?.reservationCode ?? '';
                  const tkCoupons: any[] = tk.documentInfo?.coupons ?? [];
                  return (
                    <Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
                      <Typography variant='body2' fontFamily='monospace' fontWeight={700} color='text.primary'>
                        {tk.company}-{tk.number}
                      </Typography>
                      {tkName && (
                        <>
                          <Typography variant='body2' color='text.disabled' lineHeight={1}>·</Typography>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.4}}>
                            <Person sx={{fontSize: '0.8rem', color: 'text.secondary'}} />
                            <Typography variant='body2' color='text.secondary'>{tkName}</Typography>
                          </Box>
                        </>
                      )}
                      {tkCoupons.length > 0 && (
                        <>
                          <Typography variant='body2' color='text.disabled' lineHeight={1}>·</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {tkCoupons.length} {t`coupon(s)`}
                          </Typography>
                        </>
                      )}
                      {tkPnr && (
                        <Box component='span' sx={{
                          display: 'inline-flex', alignItems: 'center',
                          px: 0.75, py: 0.2, borderRadius: '4px',
                          bgcolor: 'rgba(25,118,210,0.1)',
                          color: 'primary.dark',
                          fontFamily: 'monospace', fontWeight: 700,
                          fontSize: '0.65rem', letterSpacing: '0.04em',
                        }}>
                          PNR {tkPnr}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── Add another ticket (top) ── */}
      <Card variant='outlined' sx={{bgcolor: 'grey.50'}}>
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Typography variant='caption' color='text.secondary' sx={{fontWeight: 700, letterSpacing: '0.08em', flexShrink: 0}}>
              {t`ADD TICKET`}
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'stretch',
              border: '1px dashed',
              borderColor: addTicketError ? 'error.main' : 'rgba(0,0,0,0.3)',
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              transition: 'border-color 0.15s',
              '&:focus-within': {borderStyle: 'solid', borderColor: 'primary.main'},
            }}>
              <TextField
                inputRef={addInputRefTop}
                value={addTicketNumber}
                onChange={(e) => { setAddTicketNumber(e.target.value); setAddTicketError(null); }}
                placeholder='153-1234567890'
                variant='standard'
                InputProps={{disableUnderline: true}}
                sx={{
                  '& .MuiInputBase-input': {
                    px: 1.25, py: 0.6,
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    width: 148,
                  }
                }}
              />
              <Divider orientation='vertical' flexItem />
              <Button
                variant='text'
                size='small'
                startIcon={isSearching ? <CircularProgress size={12} color='inherit' /> : <Add />}
                onClick={handleAddAnother}
                disabled={isSearching}
                sx={{
                  borderRadius: 0, px: 1.25,
                  fontWeight: 600, fontSize: '0.75rem',
                  whiteSpace: 'nowrap', minWidth: 'auto',
                }}
              >
                {t`Add`}
              </Button>
            </Box>
          </Box>
          {addTicketError && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75}}>
              <ErrorOutline sx={{fontSize: '0.9rem'}} color='error' />
              <Typography variant='caption' color='error'>{addTicketError}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Ticket tabs (multi-ticket) ── */}
      {tickets.length > 1 && (
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <Tabs
            value={activeIndex}
            onChange={(_, v) => { setActiveIndex(v); setShowValidation(false); }}
            variant='scrollable'
            scrollButtons='auto'
          >
            {tickets.map((tk, i) => (
              <Tab
                key={i}
                value={i}
                icon={<ConfirmationNumberIcon sx={{fontSize: '0.9rem'}} />}
                iconPosition='start'
                label={tabLabel(tk.company, tk.number)}
                sx={{textTransform: 'none', minWidth: 'auto', minHeight: 44, gap: 0.5}}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* ── Active ticket card ── */}
      <Card elevation={3} sx={{borderTop: '3px solid', borderColor: 'primary.main'}}>
        <CardContent sx={{p: 3, '&:last-child': {pb: 3}}}>

          {/* Ticket header */}
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
              <Box sx={{
                background: 'linear-gradient(135deg, #3f51b5 0%, #283593 100%)',
                color: 'white',
                borderRadius: 1.5,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(63,81,181,0.4)',
              }}>
                <ConfirmationNumberIcon sx={{fontSize: '1.1rem'}} />
              </Box>
              <Box>
                <Typography variant='h6' fontWeight='bold' fontFamily='monospace' lineHeight={1.2}>
                  {ticketLabel}
                </Typography>
                {passengerName && (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25}}>
                    <Person sx={{fontSize: '0.85rem', color: 'text.secondary'}} />
                    <Typography variant='body2' color='text.secondary'>{passengerName}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <IconButton size='small' color='error' onClick={handleRemoveCurrent} sx={{mt: -0.5}}>
              <Delete fontSize='small' />
            </IconButton>
          </Box>

          {isCheckingSchedule && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1,
              bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: 1.5, mb: 2.5,
            }}>
              <CircularProgress size={16} sx={{mt: 0.25}} />
              <Typography variant='body2' color='text.secondary'>
                {t`Checking for an existing schedule…`}
              </Typography>
            </Box>
          )}

          {!isCheckingSchedule && latestSchedule && (latestSchedule.status === 'pending' || latestSchedule.status === 'processing') && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1,
              bgcolor: 'rgba(2,136,209,0.08)', border: '1px solid', borderColor: 'info.main',
              borderRadius: 1.5, p: 1.5, mb: 2.5,
            }}>
              <Schedule sx={{fontSize: '1.1rem', color: 'info.main', mt: 0.25}} />
              <Typography variant='body2' color='text.secondary'>
                {t`Already scheduled to send to`} <strong>{latestSchedule.emails.join(', ')}</strong> {t`on`}{' '}
                <strong>{DateTime.fromISO(latestSchedule.scheduledFor).toFormat('dd/MM/yyyy')}</strong>.
              </Typography>
            </Box>
          )}

          {!isCheckingSchedule && latestSchedule && latestSchedule.status === 'sent' && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1,
              bgcolor: 'rgba(46,125,50,0.08)', border: '1px solid', borderColor: 'success.main',
              borderRadius: 1.5, p: 1.5, mb: 2.5,
            }}>
              <Schedule sx={{fontSize: '1.1rem', color: 'success.main', mt: 0.25}} />
              <Typography variant='body2' color='text.secondary'>
                {t`Scheduled declaration already sent to`} <strong>{latestSchedule.emails.join(', ')}</strong>
                {latestSchedule.completedAt && <> {t`on`} <strong>{DateTime.fromISO(latestSchedule.completedAt).toFormat('dd/MM/yyyy')}</strong></>}.
              </Typography>
            </Box>
          )}

          <Divider sx={{mb: 2.5}} />

          {/* Coupons */}
          {coupons.length > 0 && (
            <Box sx={{mb: 3}}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1.5}}
              >
                {t`COUPONS / SEGMENTS`}
              </Typography>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25}}>
                {coupons.map((coupon: any, idx: number) => {
                  const status = coupon.couponStatus ?? 'O';
                  const statusLabel = COUPON_STATUS_LABELS[status] ?? status;
                  const statusColor = COUPON_STATUS_COLORS[status] ?? 'default';
                  const flightNumber = coupon.flightNumber
                    ? `${coupon.carrier ?? ''}${coupon.flightNumber}`
                    : `${coupon.carrier ?? ''}${coupon.number ?? ''}`;
                  const origin = coupon.originAirport ?? '';
                  const destination = coupon.destinationAirport ?? '';
                  const couponDate = coupon.date ?? null;
                  const couponStd = coupon.std ?? '';
                  const flownFlightNumber = coupon.flownFlightNumber
                    ? `${coupon.flownCarrier ?? ''}${coupon.flownFlightNumber}`
                    : '';
                  const flownOrigin = coupon.flownOriginAirport ?? '';
                  const flownDestination = coupon.flownDestinationAirport ?? '';
                  const flownDate = coupon.flownDate ?? null;
                  const flownStd = coupon.flownStd ?? '';
                  const newTicketNumber = coupon.newTicketNumber ?? '';
                  const borderColor = COUPON_STATUS_BORDER[status] ?? 'divider';
                  const bgColor = COUPON_STATUS_BG[status] ?? 'transparent';

                  return (
                    <Card
                      key={idx}
                      variant='outlined'
                      sx={{
                        borderRadius: 1.5,
                        borderLeft: '4px solid',
                        borderLeftColor: borderColor,
                        bgcolor: bgColor,
                      }}
                    >
                      <CardContent sx={{py: 1.75, px: 2, '&:last-child': {pb: 1.75}}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
                          <Box sx={{flex: 1, minWidth: 0}}>
                            {/* Coupon label */}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75}}>
                              <FlightTakeoff sx={{fontSize: '0.85rem', color: 'text.disabled'}} />
                              <Typography variant='caption' color='text.secondary' sx={{fontWeight: 600, letterSpacing: '0.05em'}}>
                                {t`COUPON`} {idx + 1}
                                {flightNumber && ` · ${flightNumber}`}
                              </Typography>
                            </Box>

                            {/* Route — big airport codes */}
                            {(origin || destination) && (
                              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                                <Typography
                                  variant='h6'
                                  fontWeight='bold'
                                  fontFamily='monospace'
                                  sx={{letterSpacing: '0.06em', lineHeight: 1}}
                                >
                                  {origin || '—'}
                                </Typography>
                                <Flight sx={{color: 'text.disabled', fontSize: '1rem', flexShrink: 0}} />
                                <Typography
                                  variant='h6'
                                  fontWeight='bold'
                                  fontFamily='monospace'
                                  sx={{letterSpacing: '0.06em', lineHeight: 1}}
                                >
                                  {destination || '—'}
                                </Typography>
                              </Box>
                            )}

                            {/* Date */}
                            {(couponDate || couponStd) && (
                              <Typography variant='caption' color='text.secondary'>
                                {formatCouponDateOnly(couponDate)}{couponStd && ` · ${couponStd}`}
                              </Typography>
                            )}

                            {/* Current / flown segment — always shown, can differ from issued (e.g. rebooking) */}
                            {(flownFlightNumber || flownOrigin || flownDestination || flownDate) && (
                              <Typography
                                variant='caption'
                                sx={{display: 'block', mt: 0.75, color: 'text.secondary', fontStyle: 'italic'}}
                              >
                                {t`Current`}: {flownFlightNumber}{flownFlightNumber && ' · '}
                                {flownOrigin || '—'} → {flownDestination || '—'}
                                {flownDate && ` · ${formatCouponDateOnly(flownDate)}`}
                                {flownStd && ` · ${flownStd}`}
                                {newTicketNumber && ` · ${t`New TKT`}: ${newTicketNumber}`}
                              </Typography>
                            )}
                          </Box>

                          {/* Status chip — always filled */}
                          <Chip
                            label={statusLabel}
                            color={statusColor}
                            size='small'
                            variant='filled'
                            sx={{fontWeight: 600, fontSize: '0.72rem', flexShrink: 0, mt: 0.25}}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Identity section */}
          <Box
            sx={{
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: showValidation && !isIdentityValid ? 'error.main' : 'divider',
              borderRadius: 1.5,
              p: 2,
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 2}}>
              <Person sx={{fontSize: '1.1rem', color: 'text.secondary'}} />
              <Typography variant='body2' fontWeight={600}>{t`Passenger Identity`}</Typography>
              <Typography variant='caption' color='text.secondary'>({t`at least one required`})</Typography>
            </Box>
            <Grid2 container spacing={2}>
              <Grid2 size={{xs: 12, sm: 4}}>
                <FormControl fullWidth size='small'>
                  <InputLabel>{t`Document Type`}</InputLabel>
                  <Select
                    value={documentType}
                    label={t`Document Type`}
                    onChange={(e) => updateField('documentType', e.target.value)}
                  >
                    {DOCUMENT_TYPES.map((dt) => (
                      <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{xs: 12, sm: 4}}>
                <TextField
                  label={t`ID Number`}
                  value={idNumber}
                  onChange={(e) => updateField('idNumber', e.target.value)}
                  fullWidth
                  size='small'
                  error={showValidation && !isIdentityValid}
                />
              </Grid2>
              <Grid2 size={{xs: 12, sm: 4}}>
                <TextField
                  label={t`NIF`}
                  value={nif}
                  onChange={(e) => updateField('nif', e.target.value)}
                  fullWidth
                  size='small'
                  error={showValidation && !isIdentityValid}
                />
              </Grid2>
            </Grid2>
            {showValidation && !isIdentityValid && (
              <Typography variant='caption' color='error' sx={{mt: 1.25, display: 'block'}}>
                {t`At least one of the following fields is required for each ticket: ID Number or NIF.`}
              </Typography>
            )}
          </Box>

        </CardContent>
      </Card>

      {/* ── Order summary + add ticket ── */}
      <Card variant='outlined' sx={{bgcolor: 'grey.50'}}>
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Typography variant='caption' color='text.secondary' sx={{fontWeight: 700, letterSpacing: '0.08em', flexShrink: 0}}>
              {t`ORDER`}
            </Typography>
            {tickets.map((tk, i) => {
              const name = getPassengerName(tk.documentInfo?.passenger);
              return (
                <Chip
                  key={i}
                  icon={<ConfirmationNumberIcon sx={{fontSize: '0.85rem !important'}} />}
                  label={name ? `${tk.company}-${tk.number} · ${name}` : `${tk.company}-${tk.number}`}
                  size='small'
                  variant={i === activeIndex ? 'filled' : 'outlined'}
                  color={i === activeIndex ? 'primary' : 'default'}
                  onClick={() => { setActiveIndex(i); setShowValidation(false); }}
                  sx={{fontSize: '0.72rem', fontFamily: 'monospace', cursor: 'pointer'}}
                />
              );
            })}

            {/* Add ticket pill — inline with the order chips */}
            <Box sx={{
              display: 'flex', alignItems: 'stretch',
              border: '1px dashed',
              borderColor: addTicketError ? 'error.main' : 'rgba(0,0,0,0.3)',
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              transition: 'border-color 0.15s',
              '&:focus-within': {borderStyle: 'solid', borderColor: 'primary.main'},
            }}>
              <TextField
                inputRef={addInputRef}
                value={addTicketNumber}
                onChange={(e) => { setAddTicketNumber(e.target.value); setAddTicketError(null); }}
                placeholder='153-1234567890'
                variant='standard'
                InputProps={{disableUnderline: true}}
                sx={{
                  '& .MuiInputBase-input': {
                    px: 1.25, py: 0.6,
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    width: 148,
                  }
                }}
              />
              <Divider orientation='vertical' flexItem />
              <Button
                variant='text'
                size='small'
                startIcon={isSearching ? <CircularProgress size={12} color='inherit' /> : <Add />}
                onClick={handleAddAnother}
                disabled={isSearching}
                sx={{
                  borderRadius: 0, px: 1.25,
                  fontWeight: 600, fontSize: '0.75rem',
                  whiteSpace: 'nowrap', minWidth: 'auto',
                }}
              >
                {t`Add`}
              </Button>
            </Box>
          </Box>

          {addTicketError && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75}}>
              <ErrorOutline sx={{fontSize: '0.9rem'}} color='error' />
              <Typography variant='caption' color='error'>{addTicketError}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider />

      {/* ── Footer ── */}
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Button variant='text' startIcon={<ArrowBack />} sx={{color: 'text.secondary'}} onClick={() => navigate('/declarations/ticket-usage')}>
          {t`Back`}
        </Button>
        <Button variant='contained' color='primary' onClick={handleContinue} disabled={isSearching}>
          {t`Continue to Declaration`}
        </Button>
      </Box>

    </Box>
  );
};

export default TicketUsageResultPage;
